<template>
    <Head :title="$t('apartments.apartment') + ' ' + apartment.number" />
    <div
        class="max-w-3xl mx-auto px-4 sm:px-6 md:space-x-5 lg:max-w-7xl lg:px-5 overflow-y-auto"
    >
        <div class="ml-4 mb-4">
            <piped-title
                :first="$t('apartments.apartment') + ' ' + apartment.number"
                :second="$t('apartments.overview')"
                :pages="breadcrumbPages"
            ></piped-title>
        </div>

        <!-- Header -->
        <apartments-header
            :apartment="apartment"
            :project="project"
            :buyers="buyers"
            :edit-base-link="
                route('advisors.apartments.edit.base', { apartment: apartment.uuid })
            "
            :create-buyer-link="
                route('advisors.apartments.create.buyer', { apartment: apartment.uuid })
            "
            edit-buyer-route="advisors.apartments.edit.buyer"
            resend-buyer-invite-link="advisors.apartments.resendInvite"
            delete-buyer-route="advisors.apartments.delete.buyer"
            :edit-advisor-link="
                route('advisors.apartments.edit.advisor', { apartment: apartment.uuid })
            "
        ></apartments-header>

        <!-- Info Bar -->
        <apartments-infobar
            :apartment="apartment"
            :topics="topics"
            :deadlines-missed="deadlinesMissed"
            :deletable="deletable"
            :on-delete="openDeleteModal"
            edit-state-route="advisors.apartments.edit.state"
            manage-topics-route="advisors.apartments.manageTopics"
        />

        <!-- Beide Boxen strecken sich auf gleiche Höhe (items-stretch + flex-col
             in den Komponenten), damit sie unten bündig abschliessen. -->
        <div class="lg:flex lg:gap-6 lg:items-stretch">
            <div class="w-full lg:w-1/2 flex">
                <!-- Pending Approvals -->
                <apartments-pending-approvals
                    :buyers="buyers"
                    :pending-approvals="pendingApprovals"
                    :can-remind="isRemindable"
                    :last-reminded="lastReminded"
                    :buyers-not-reachable="buyersNotReachable"
                    :send-reminder-route="sendReminderRoute"
                >
                </apartments-pending-approvals>
            </div>
            <div class="w-full lg:w-1/2 flex">
                <!-- Notes (Thema 7) -->
                <apartments-notes
                    :apartment="apartment"
                    :notes="notes"
                    :notes-link="notesLink"
                >
                </apartments-notes>
            </div>
        </div>

        <!-- Budget -->
        <apartments-budget
            :apartment="apartment"
            :topics="topics"
            :topicCosts="topicCosts"
            :is-budget-based="feeCalculation ? feeCalculation.isBudgetBased : false"
            :is-advisor="is_advisor"
            :visibility="visibility"
            :columns-settings-link="columnsSettingsLink"
            :budget-export-link="
                route('advisors.apartments.budget.export', { apartment: apartment.uuid })
            "
        ></apartments-budget>

        <!-- Documents-->
        <document-table :documents="documents"></document-table>
    </div>

    <jet-confirmation-modal :show="showingDelete">
        <template #title>{{ $t("apartments.deleteApartmentTitle") }}</template>

        <template #content>
            {{ $t("apartments.deleteApartmentDesc") }}
        </template>

        <template #footer>
            <button
                @click="cancelDeleteModal"
                type="button"
                class="inline-block uppercase font-semibold text-xs tracking-widest"
            >
                {{ $t("actions.cancel") }}
            </button>

            <jet-danger-button class="ml-2" @click="deleteApartment()">
                {{ $t("actions.delete") }}
            </jet-danger-button>
        </template>
    </jet-confirmation-modal>
</template>

<script>
import { defineComponent } from "vue";
import ApartmentLayout from "@/Layouts/ApartmentLayout";
import PipedTitle from "@/Shared/Components/PipedTitle";
import { Link, Head } from "@inertiajs/inertia-vue3";
import DocumentTable from "@/Shared/Components/Documents/DocumentTable";
import ApartmentsHeader from "@/Shared/Components/Apartments/ApartmentsHeader";
import ApartmentsInfobar from "@/Shared/Components/Apartments/ApartmentsInfobar";
import ApartmentsPendingApprovals from "@/Shared/Components/Apartments/ApartmentsPendingApprovals";
import ApartmentsNotes from "@/Shared/Components/Apartments/ApartmentsNotes";
import ApartmentsBudget from "@/Shared/Components/Apartments/ApartmentsBudget";
import JetConfirmationModal from "@/Jetstream/ConfirmationModal.vue";
import JetDangerButton from "@/Jetstream/DangerButton.vue";

export default defineComponent({
    props: [
        "apartment",
        "project",
        "buyers",
        "deletable",
        "topics",
        "pendingApprovals",
        "topicCosts",
        // Auftrag Luca, 2026-08-17: steuert die Mehrkosten-Spalte der
        // Abrechnungstabelle (Seiten-Prop, nicht HandleInertiaRequests).
        "feeCalculation",
        "documents",
        "deadlinesMissed",
        "isRemindable",
        "lastReminded",
        "buyersNotReachable",
        "sendReminderRoute",
        "notes",
        "notesLink",
        "is_advisor",
        "visibility",
        "columnsSettingsLink",
    ],
    data() {
        return {
            showingDelete: false,
        };
    },
    components: {
        Head,
        JetConfirmationModal,
        DocumentTable,
        PipedTitle,
        Link,
        ApartmentsHeader,
        ApartmentsInfobar,
        ApartmentsPendingApprovals,
        ApartmentsNotes,
        ApartmentsBudget,
        JetDangerButton,
    },
    layout: ApartmentLayout,
    methods: {
        openDeleteModal() {
            this.showingDelete = true;
        },
        deleteApartment() {
            this.$inertia.delete(
                this.route("advisors.projects.apartments.delete", {
                    project: this.project.uuid,
                    apartment: this.apartment.uuid,
                }),
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "apartments.deleted",
                        });
                    },
                }
            );
        },
        cancelDeleteModal() {
            this.showingDelete = false;
        },
    },
    computed: {
        breadcrumbPages() {
            return [
                {
                    name: this.$t("projects.projects"),
                    href: this.route("advisors.projects.index"),
                    current: false,
                },
                {
                    name: this.project.name,
                    href: this.route("advisors.projects.show", {
                        uuid: this.project.uuid,
                    }),
                    current: false,
                },
                {
                    name: `${this.$t("apartments.apartment")} ${this.apartment.number}`,
                    href: this.route("advisors.apartments.show", this.apartment.uuid),
                    current: true,
                },
            ];
        },
    },
});
</script>

<style scoped></style>
